import { useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { getInlineLoader } from "../Helpers";
import banner1 from "../assets/banner1.jpg";
import banner5 from "../assets/banner4.jpg";
import banner7 from "../assets/banner3.jpg";
import { FaBuilding, FaBriefcase, FaCalendarAlt, FaArrowRight } from "react-icons/fa";

const clientList = [
  "ISS Global",
  "PSL", 
  "Amsons Group",
  "TIB Development Bank", 
  "SAPPF Ltd",
  "AFICL",
  "NMB Bank",
  "CRDB Bank"
];

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useState(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// Client card component
const ClientCard = ({ client, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div 
        className="card border-0 h-100 rounded"
        style={{
          background: "white",
          borderRadius: "14px",
          boxShadow: isHovered 
            ? "0 12px 40px rgba(59, 130, 246, 0.15)" 
            : "0 2px 12px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${isHovered ? "rgba(59,130,246,0.3)" : "rgba(0,0,0,0.05)"}`,
          transition: "all 0.3s ease"
        }}
        whileHover={{ y: -4 }}
      >
        <div className="card-body rounded p-4 d-flex justify-content-between align-items-center">
          <span className="fw-semibold" style={{ 
            color: "#0f172a", 
            fontSize: "1rem",
            letterSpacing: "0.2px"
          }}>
            {client}
          </span>
          <motion.span 
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "32px", height: "32px",
              background: "linear-gradient(135deg, #3b82f6, #6366f1)",
              color: "white",
              fontWeight: "600",
              fontSize: "0.75rem",
              boxShadow: "0 4px 12px rgba(59,130,246,0.3)"
            }}
            animate={{ scale: isHovered ? 1.1 : 1 }}
          >
            {String(index + 1).padStart(2, '0')}
          </motion.span>
        </div>
        {/* Subtle bottom accent line */}
        <motion.div 
          style={{
            position: "absolute", bottom: 0, left: "20%", right: "20%", height: "2px",
            background: "linear-gradient(90deg, transparent, #3b82f6, transparent)",
            borderRadius: "2px",
            opacity: isHovered ? 1 : 0
          }}
        />
      </motion.div>
    </motion.div>
  );
};

export default function HomeBanner3() {
  const [ready, setReady] = useState(false);
  const [stats, setStats] = useState({ clients: 0, projects: 0, years: 0 });

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    const timer = setTimeout(() => {
      setStats({ clients: 50, projects: 200, years: 8 });
    }, 500);
    return () => clearTimeout(timer);
  }, [ready]);

  if (!ready) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <section 
      className="position-relative overflow-hidden py-5"
      style={{
        background: `
          radial-gradient(ellipse at top right, rgba(59,130,246,0.06) 0%, transparent 60%),
          radial-gradient(ellipse at bottom left, rgba(139,92,246,0.06) 0%, transparent 60%),
          linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)
        `
      }}
    >
      {/* Subtle decorative elements */}
      <div className="position-absolute" style={{
        top: "10%", right: "5%", width: "200px", height: "200px",
        background: "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)",
        borderRadius: "50%", filter: "blur(30px)", pointerEvents: "none"
      }} />
      
      <div className="container position-relative">
        <div className="row align-items-center g-5">
          
          {/* Left Column: Heading, Text, Stats */}
          <div className="col-lg-6">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Eyebrow badge */}
              <span 
                className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill mb-4 font-bold"
                style={{
                  background: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(20,184,166,0.08))",
                  color: "#20283a",
                  fontWeight: "600",
                  fontSize: "0.82rem",
                  border: "1px solid rgba(16,185,129,0.25)",
                  letterSpacing: "1px"
                }}
              >
                <FaBuilding size={14} />
                TRUSTED PARTNERS
              </span>

              {/* Main heading */}
              <h2 
                className="font-bold mb-4"
                style={{ 
                  fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
                  color: "#0f172a",
                  lineHeight: "1.2",
                  letterSpacing: "-0.02em"
                }}
              >
                Serving Tanzania's <br/>
                <span style={{ 
                  background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}>
                  Leading Organizations
                </span>
              </h2>
              
              {/* Description */}
              <p className="mb-5" style={{ 
                fontSize: "1.1rem", 
                color: "#475569", 
                lineHeight: "1.7",
                maxWidth: "520px"
              }}>
                Partnering with financial institutions, international organizations, manufacturing, 
                and development sectors across the country.
              </p>

              {/* Stats Cards - Modern Design */}
              <div className="row g-3 mb-5">
                {/* Clients Stat */}
                <motion.div 
                  className="col-md-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="text-center p-4 rounded-4" style={{
                    background: "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(99,102,241,0.08))",
                    border: "1px solid rgba(59,130,246,0.2)",
                    boxShadow: "0 4px 20px rgba(59,130,246,0.08)"
                  }}>
                    <FaBuilding size={24} className="mb-2" style={{ color: "#3b82f6" }} />
                    <div className="fw-bold mb-1" style={{ 
                      fontSize: "1.8rem", 
                      color: "#0f172a",
                      lineHeight: 1 
                    }}>
                      <AnimatedCounter end={stats.clients} suffix="+" />
                    </div>
                    <div className="text-muted small text-uppercase fw-semibold" style={{ 
                      fontSize: "0.7rem", 
                      letterSpacing: "0.8px" 
                    }}>
                      Clients
                    </div>
                  </div>
                </motion.div>
                
                {/* Projects Stat */}
                <motion.div 
                  className="col-md-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="text-center p-4 rounded-4" style={{
                    background: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(20,184,166,0.08))",
                    border: "1px solid rgba(16,185,129,0.2)",
                    boxShadow: "0 4px 20px rgba(16,185,129,0.08)"
                  }}>
                    <FaBriefcase size={24} className="mb-2" style={{ color: "#10b981" }} />
                    <div className="fw-bold mb-1" style={{ 
                      fontSize: "1.8rem", 
                      color: "#0f172a",
                      lineHeight: 1 
                    }}>
                      <AnimatedCounter end={stats.projects} />
                    </div>
                    <div className="text-muted small text-uppercase fw-semibold" style={{ 
                      fontSize: "0.7rem", 
                      letterSpacing: "0.8px" 
                    }}>
                      Projects
                    </div>
                  </div>
                </motion.div>
                
                {/* Years Stat */}
                <motion.div 
                  className="col-md-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="text-center p-4 rounded-4" style={{
                    background: "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(168,85,247,0.08))",
                    border: "1px solid rgba(139,92,246,0.2)",
                    boxShadow: "0 4px 20px rgba(139,92,246,0.08)"
                  }}>
                    <FaCalendarAlt size={24} className="mb-2" style={{ color: "#8b5cf6" }} />
                    <div className="fw-bold mb-1" style={{ 
                      fontSize: "1.8rem", 
                      color: "#0f172a",
                      lineHeight: 1 
                    }}>
                      <AnimatedCounter end={stats.years} suffix="+" />
                    </div>
                    <div className="text-muted small text-uppercase fw-semibold" style={{ 
                      fontSize: "0.7rem", 
                      letterSpacing: "0.8px" 
                    }}>
                      Years
                    </div>
                  </div>
                </motion.div>
              </div>

            </motion.div>
          </div>

          {/* Right Column: Client Grid */}
          <div className="col-lg-6">
            <motion.div 
              className="row g-3"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                visible: { transition: { staggerChildren: 0.05 } }
              }}
            >
              {clientList.map((client, index) => (
                <div className="col-md-6 h-100 rounded" key={client}>
                  <ClientCard client={client} index={index} />
                </div>
              ))}
            </motion.div>
          </div>
          
        </div>
      </div>

      {/* Global Styles */}
      <style>{`
        .font-bold { font-weight: 700 !important; }
        .rounded-4 { border-radius: 16px !important; }
        
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
        
        /* Smooth scrollbar */
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #f8fafc; }
        ::-webkit-scrollbar-thumb { 
          background: linear-gradient(180deg, #cbd5e1, #94a3b8); 
          border-radius: 4px; 
        }
        
        /* Focus accessibility */
        a:focus-visible, button:focus-visible {
          outline: 3px solid #3b82f6;
          outline-offset: 2px;
          border-radius: 8px;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          section { padding: 3rem 1rem !important; }
          .col-4 { flex: 0 0 33.333% !important; max-width: 33.333% !important; }
          .card-body.p-4 { padding: 1.25rem !important; }
        }
        
        @media (max-width: 480px) {
          .col-4 { flex: 0 0 100% !important; max-width: 100% !important; margin-bottom: 1rem; }
          .row.g-3.mb-5 { flex-direction: column; align-items: center; }
        }
      `}</style>
    </section>
  );
}